// Frameworks + Production catalog data. Lesson ids/titles are transcribed
// from backend/src/content/lessonContent.js's `technique` field (the single
// source of truth for lesson content, per CLAUDE.md) -- this file only
// supplies the list/ordering/display metadata the catalog UI needs, the same
// role Challenges.tsx's BASIC_CHALLENGES plays for the Tier-1 track. Keep
// titles in sync with lessonContent.js if a lesson is ever renamed there.
//
// `slug` doubles as the `topic` value sent to POST /progress/sync (see
// Lesson.tsx's topicForLesson()) and the key read back from GET /progress's
// topicProgress map -- both are topic-agnostic on the backend (memory.md,
// 2026-08-29), so any stable slug works as long as it's used consistently.

export interface TrackLesson {
  id: string;
  title: string;
}

export interface TrackDefinition {
  slug: string;
  trackNumber: number;
  name: string;
  category: 'framework' | 'production';
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedTime: string;
  description: string;
  whyItMatters: string;
  outcomes: string[];
  tags: string[];
  lessons: TrackLesson[];
}

export const FRAMEWORK_TRACKS: TrackDefinition[] = [
  {
    slug: 'langchain',
    trackNumber: 1,
    name: 'LangChain',
    category: 'framework',
    difficulty: 'Beginner',
    estimatedTime: '45 mins',
    description: 'The standard way to build a single real agent: create_agent, tools, memory, and middleware.',
    whyItMatters: 'The foundational harness for single-agent systems: wiring LLMs, tools, structured output, and state stores into production code.',
    outcomes: [
      'Master the create_agent harness (Agent = Model + Tools + System Prompt)',
      'Build typed, validated tool calls with @tool and ToolRuntime',
      'Manage conversation memory across turns with checkpointers and long-term stores'
    ],
    tags: ['Agents & tools', 'Memory', 'Multi-agent'],
    lessons: [
      { id: 'langchain-1', title: 'create_agent mental model (Agent = Model + Harness)' },
      { id: 'langchain-2', title: 'Provider-agnostic models with init_chat_model' },
      { id: 'langchain-3', title: 'Tools: @tool, typed args, and ToolRuntime' },
      { id: 'langchain-4', title: 'Messages, content_blocks, and structured output' },
      { id: 'langchain-5', title: 'Memory: short-term (checkpointer) and long-term (store)' },
      { id: 'langchain-6', title: 'Middleware: before_model, after_model, wrap_tool_call' },
      { id: 'langchain-7', title: 'Multi-agent patterns: subagents, handoffs, and routing' },
      { id: 'langchain-8', title: 'Capstone: build, trace, and debug an agent' },
      { id: 'langchain-9', title: 'RAG: wrapping a retriever as a tool with create_retriever_tool' },
    ],
  },
  {
    slug: 'langgraph',
    trackNumber: 2,
    name: 'LangGraph',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '60 mins',
    description: "What Whiskey's own agent backend runs on -- state graphs, persistence, and human-in-the-loop control.",
    whyItMatters: 'The industry gold standard for complex, cyclic multi-step agents, deterministic validation branches, and human approvals.',
    outcomes: [
      'Construct cyclic state graphs with StateGraph and TypedDict schemas',
      'Manage multi-branch state merges using custom reducers and channels',
      'Implement human-in-the-loop breakpoints with interrupt() and Command(resume=...)'
    ],
    tags: ['State graphs', 'Persistence', 'Human-in-the-loop'],
    lessons: [
      { id: 'langgraph-1', title: 'When to drop to StateGraph instead of create_agent' },
      { id: 'langgraph-2', title: 'StateGraph core loop: state schema, nodes, edges, compile()' },
      { id: 'langgraph-3', title: 'Reducers and parallel-branch state merging' },
      { id: 'langgraph-4', title: 'Persistence: checkpointers vs. stores' },
      { id: 'langgraph-5', title: 'Human-in-the-loop via interrupt() and Command(resume=...)' },
      { id: 'langgraph-6', title: 'The prebuilt ReAct pattern, built by hand' },
      { id: 'langgraph-7', title: 'Capstone: a custom workflow mixing deterministic and agentic nodes' },
      { id: 'langgraph-8', title: 'Streaming graph execution: stream_mode values, updates, and messages' },
    ],
  },
  {
    slug: 'crewai',
    trackNumber: 3,
    name: 'CrewAI',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '75 mins',
    description: 'Teams of specialist agents with roles, goals, and delegated tasks, plus event-driven Flows.',
    whyItMatters: 'Orchestrate collaborative teams of specialized agents with autonomous delegation, role-playing, and event-driven Flows.',
    outcomes: [
      'Configure autonomous specialist agents with distinct roles, goals, and backstories',
      'Chain tasks sequentially and hierarchically with manager delegation and review',
      'Wrap agent crews in event-driven Flows with @start, @listen, and @router'
    ],
    tags: ['Roles & goals', 'Flows', 'Delegation'],
    lessons: [
      { id: 'crewai-1', title: 'CrewAI mental model: teams of specialists vs. state machines' },
      { id: 'crewai-2', title: 'Your first Agent: role, goal, backstory, LLM binding' },
      { id: 'crewai-3', title: 'Tasks: description, expected_output, context chaining' },
      { id: 'crewai-4', title: 'Your first Crew: assembling agents and tasks, Sequential process' },
      { id: 'crewai-5', title: 'Giving agents tools: the @tool decorator' },
      { id: 'crewai-6', title: 'Custom tools: BaseTool and Pydantic args_schema' },
      { id: 'crewai-7', title: 'Hierarchical process: manager delegation and review' },
      { id: 'crewai-8', title: 'Memory in CrewAI' },
      { id: 'crewai-9', title: 'Introducing Flows: @start/@listen/@router wrapping a Crew' },
      { id: 'crewai-10', title: 'Structured outputs and human-in-the-loop checkpoints in Flows' },
      { id: 'crewai-11', title: 'Debugging a Crew: verbose mode and task callbacks' },
      { id: 'crewai-12', title: 'Capstone: multi-crew Flow with conditional routing' },
      { id: 'crewai-13', title: 'Knowledge sources: attaching reference material to a Crew' },
    ],
  },
  {
    slug: 'maf',
    trackNumber: 4,
    name: 'Microsoft Agent Framework',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '70 mins',
    description: "AutoGen's GA successor -- workflow orchestration patterns and human-in-the-loop control.",
    whyItMatters: 'Enterprise-grade conversation orchestration, group chat patterns, and resilient workflow durability for mission-critical software.',
    outcomes: [
      'Orchestrate multi-agent group chats, handoffs, and magentic planning',
      'Implement tool approval checkpoints and interactive human feedback',
      'Ensure enterprise durability and state checkpointing across turns'
    ],
    tags: ['Workflows', 'Orchestration', 'Human-in-the-loop'],
    lessons: [
      { id: 'maf-1', title: 'Multi-agent conversation as a paradigm: AutoGen to MAF' },
      { id: 'maf-2', title: 'MAF fundamentals: ChatAgent and model clients' },
      { id: 'maf-3', title: 'Giving agents tools in MAF' },
      { id: 'maf-4', title: 'Sequential workflow orchestration' },
      { id: 'maf-5', title: 'Concurrent workflow orchestration' },
      { id: 'maf-6', title: 'Group Chat orchestration' },
      { id: 'maf-7', title: 'Handoff orchestration' },
      { id: 'maf-8', title: 'Magentic orchestration: planning managers for open-ended problems' },
      { id: 'maf-9', title: 'Human-in-the-loop: tool approval and request-info pauses' },
      { id: 'maf-10', title: 'Durability and checkpointing for long-running workflows' },
      { id: 'maf-11', title: '(Context module) Classic AutoGen concepts and migration' },
      { id: 'maf-12', title: 'Capstone: a multi-pattern workflow' },
    ],
  },
  {
    slug: 'strands',
    trackNumber: 5,
    name: 'AWS Strands Agents SDK',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '55 mins',
    description: 'A model-driven agent loop with real, sandbox-executed code -- no simulated grading.',
    whyItMatters: 'Run model-driven agent loops with real code sandbox execution and native Model Context Protocol (MCP) integrations.',
    outcomes: [
      'Connect external tools and servers via Model Context Protocol (MCPClient)',
      'Persist agent sessions using FileSessionManager',
      'Build swarm and agent-as-tool architectures with real OpenTelemetry traces'
    ],
    tags: ['Tools', 'Model-driven loop', 'Real execution'],
    lessons: [
      { id: 'strands-1', title: 'Strands Agents: the @tool decorator and a model-driven loop' },
      { id: 'strands-2', title: 'Strands Agents: MCP tool integration with MCPClient' },
      { id: 'strands-3', title: 'Strands Agents: persistent sessions with FileSessionManager' },
      { id: 'strands-4', title: 'Strands Agents: streaming responses with stream_async' },
      { id: 'strands-5', title: 'Strands Agents: structured output with structured_output_model' },
      { id: 'strands-6', title: 'Strands Agents: Agents-as-Tools' },
      { id: 'strands-7', title: 'Strands Agents: Swarm' },
      { id: 'strands-8', title: 'Strands Agents: Graph' },
      { id: 'strands-9', title: 'Strands Agents: production observability with StrandsTelemetry' },
      { id: 'strands-10', title: 'Strands Agents capstone: tools, agents-as-tools, and persisted sessions together' },
    ],
  },
  {
    slug: 'openaiagents',
    trackNumber: 6,
    name: 'OpenAI Agents SDK',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '45 mins',
    description: 'Tools, structured output, sessions, handoffs, guardrails, and streaming with a lightweight, provider-agnostic SDK.',
    whyItMatters: 'Clean, lightweight, provider-agnostic agent primitives with native handoffs and input/output guardrails.',
    outcomes: [
      'Define plain function tools with @function_tool and Runner.run',
      'Wire seamless multi-agent handoffs between triage and specialist agents',
      'Implement safety tripwires and input/output guardrails'
    ],
    tags: ['Tools', 'Guardrails', 'Multi-agent'],
    lessons: [
      { id: 'openaiagents-1', title: 'OpenAI Agents SDK fundamentals: @function_tool and Runner.run' },
      { id: 'openaiagents-2', title: 'OpenAI Agents SDK structured output with output_type' },
      { id: 'openaiagents-3', title: 'OpenAI Agents SDK persistent memory with SQLiteSession' },
      { id: 'openaiagents-4', title: 'OpenAI Agents SDK multi-agent handoffs' },
      { id: 'openaiagents-5', title: 'OpenAI Agents SDK guardrails: input and output tripwires' },
      { id: 'openaiagents-6', title: 'OpenAI Agents SDK streaming with Runner.run_streamed' },
      { id: 'openaiagents-7', title: 'OpenAI Agents SDK capstone: tools, handoffs, guardrails, and sessions together' },
    ],
  },
  {
    slug: 'adk',
    trackNumber: 7,
    name: 'Google Agent Development Kit (ADK)',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '45 mins',
    description: 'Plain-function tools, automatic multi-agent delegation, and code-level guardrails from Google\'s own agent framework.',
    whyItMatters: "Google's modular agent framework featuring automatic delegation, tool state persistence, and callback guardrails.",
    outcomes: [
      'Build agents with plain-function tools and execution runners',
      'Manage cross-turn tool state using tool_context.state',
      'Configure automatic sub-agent delegation and safety callbacks'
    ],
    tags: ['Tools', 'Multi-agent', 'Guardrails'],
    lessons: [
      { id: 'adk-1', title: 'Google ADK fundamentals: plain-function tools with Agent + Runner.run_async' },
      { id: 'adk-2', title: 'Google ADK tool state: reading and writing tool_context.state across turns' },
      { id: 'adk-3', title: 'Google ADK structured output: output_schema and output_key' },
      { id: 'adk-4', title: 'Google ADK multi-agent delegation: sub_agents and automatic transfer_to_agent' },
      { id: 'adk-5', title: 'Google ADK agent-as-tool: AgentTool for a bounded call instead of a full handoff' },
      { id: 'adk-6', title: 'Google ADK guardrails: before_model_callback and before_tool_callback' },
      { id: 'adk-7', title: 'Google ADK capstone: tools, sub_agents delegation, state, and a guardrail together' },
    ],
  },
  {
    slug: 'llamaindex',
    trackNumber: 8,
    name: 'LlamaIndex',
    category: 'framework',
    difficulty: 'Intermediate',
    estimatedTime: '40 mins',
    description: 'Agents with indexing and retrieval built in -- FunctionAgent, native RAG, and multi-agent handoff.',
    whyItMatters: 'Agentic RAG combining high-performance retrieval index engines with autonomous function-calling loops.',
    outcomes: [
      'Wrap VectorStoreIndex retrievers as dynamic agent tools',
      'Build multi-agent workflows with native handoffs',
      'Persist contextual state across retrieval turns'
    ],
    tags: ['Tools', 'RAG', 'Multi-agent'],
    lessons: [
      { id: 'llamaindex-1', title: 'LlamaIndex FunctionAgent: tools as plain functions, no decorator' },
      { id: 'llamaindex-2', title: 'LlamaIndex RAG: VectorStoreIndex and QueryEngineTool' },
      { id: 'llamaindex-3', title: 'LlamaIndex multi-agent handoff with AgentWorkflow' },
      { id: 'llamaindex-4', title: 'LlamaIndex persisted state with Context' },
      { id: 'llamaindex-5', title: 'LlamaIndex streaming with stream_events' },
      { id: 'llamaindex-6', title: 'LlamaIndex capstone: RAG tool, multi-agent handoff, and persisted state together' },
    ],
  },
  {
    slug: 'agentcore',
    trackNumber: 9,
    name: 'AWS Bedrock AgentCore',
    category: 'framework',
    difficulty: 'Advanced',
    estimatedTime: '30 mins',
    description: 'Shipping an agent, not just building one -- Runtime deployment, managed memory, and secure third-party access.',
    whyItMatters: 'Deploying enterprise agents into managed cloud runtimes with isolated memory and third-party credential vending.',
    outcomes: [
      'Deploy agents with BedrockAgentCoreApp runtime',
      'Manage short-term events and long-term memory retrieval',
      'Secure tool invocations with OAuth token vending'
    ],
    tags: ['Deployment', 'Memory', 'Identity'],
    lessons: [
      { id: 'agentcore-1', title: 'AgentCore Runtime: deploying an agent with BedrockAgentCoreApp' },
      { id: 'agentcore-2', title: 'AgentCore Memory: short-term events and long-term retrieval with MemoryClient' },
      { id: 'agentcore-3', title: 'AgentCore Identity: vending third-party tokens with requires_access_token' },
    ],
  },
];

export const PRODUCTION_TRACK: TrackDefinition = {
  slug: 'production',
  trackNumber: 10,
  name: 'Production & Observability',
  category: 'production',
  difficulty: 'Advanced',
  estimatedTime: '75 mins',
  description: 'Building an agent is one skill. Shipping one you can trust is another.',
  whyItMatters: 'Bridge the chasm between demo agents and production systems with distributed tracing, automated evals, and deterministic guardrails.',
  outcomes: [
    'Instrument agents with OpenTelemetry traces to isolate root-cause latency and errors',
    'Set up LLM-as-a-judge and RAG faithfulness evaluations (RAGAS)',
    'Implement deterministic output validators and dialog guardrails (Guardrails AI, NeMo)'
  ],
  tags: ['Tracing', 'Evaluation', 'Guardrails'],
  lessons: [
    { id: 'production-1', title: 'Why production agents need more than "it worked in my notebook"' },
    { id: 'production-2', title: 'Tracing fundamentals' },
    { id: 'production-3', title: 'Debugging a broken agent from a trace: root cause vs. symptom' },
    { id: 'production-4', title: 'Evaluation fundamentals: LLM-as-judge' },
    { id: 'production-5', title: 'RAG evaluation with RAGAS: faithfulness' },
    { id: 'production-6', title: 'Regression testing agents with promptfoo' },
    { id: 'production-7', title: 'Guardrails fundamentals: prompt-based patterns and their real limits' },
    { id: 'production-8', title: 'Output validation with Guardrails AI' },
    { id: 'production-9', title: 'Dialog-flow safety with NeMo Guardrails' },
    { id: 'production-10', title: 'Deployment-readiness properties' },
    { id: 'production-11', title: 'Capstone: instrument an agent with tracing, evaluation, and a guardrail' },
    { id: 'production-12', title: 'Cost and latency optimization: prompt caching and cross-region inference' },
  ],
};

export const ALL_TRACKS: TrackDefinition[] = [...FRAMEWORK_TRACKS, PRODUCTION_TRACK];

export function getTrack(slug: string | undefined): TrackDefinition | undefined {
  return ALL_TRACKS.find((t) => t.slug === slug);
}
